import React, { useState } from "react";
import { MessageSquarePlus, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

// Always-available feedback entry point, rendered app-wide.
export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", feedback: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.feedback.trim()) {
      toast.error("Please share a little feedback before sending.");
      return;
    }
    setSending(true);
    try {
      await api.post("/feedback", form);
      toast.success("Thank you for the feedback! 💛");
      setForm({ name: "", email: "", feedback: "" });
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send feedback. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="global-feedback-button"
          title="Share feedback"
          className="fixed bottom-4 right-4 z-[60] inline-flex items-center gap-2 rounded-full
                     bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white shadow-md
                     transition-colors hover:bg-indigo-500"
        >
          <MessageSquarePlus className="w-4 h-4" />
          <span className="hidden sm:inline">Feedback</span>
        </button>
      </DialogTrigger>
      <DialogContent data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle>Share your feedback</DialogTitle>
          <DialogDescription>
            Tell us what you love or what could be better. Name and email are optional.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="fb-name">Name</Label>
              <Input id="fb-name" value={form.name} onChange={set("name")} placeholder="Optional"
                     className="mt-1.5" data-testid="feedback-name-input" />
            </div>
            <div>
              <Label htmlFor="fb-email">Email</Label>
              <Input id="fb-email" type="email" value={form.email} onChange={set("email")} placeholder="Optional"
                     className="mt-1.5" data-testid="feedback-email-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="fb-message">Feedback</Label>
            <Textarea id="fb-message" value={form.feedback} onChange={set("feedback")} rows={5}
                      placeholder="What's on your mind?" className="mt-1.5" data-testid="feedback-message-input" />
          </div>
          <button type="submit" disabled={sending}
                  className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="feedback-submit-button">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? "Sending…" : "Send feedback"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
